/**
 * rejectedAlternativesService — data access for P2's structural alternatives.
 *
 * PRD: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md §4.
 * Schema + RPC: supabase/migrations/702_decision_rejected_alternatives.sql
 * (applied to staging and prod 2026-09-02).
 * Judgment (below-cutline predicate, narration): src/lib/rejectedAlternatives.ts.
 *
 * Writes go through `record_rejected_alternatives` only (Build Contract §2.3a).
 * This module never inserts alternative rows — the point of the phase is that
 * the record is derived from the pool's own state rather than asserted by a
 * client, and an insert path here would quietly reintroduce the assertion.
 *
 * Fully on the typed client, so schema drift on this table is a compile error
 * (the DB contract). `comparison_set_kind` is the one field still narrowed by
 * hand: the column is a CHECK-constrained `text`, which generates as `string`,
 * and the two legal values are the union in rejectedAlternatives.ts. A row
 * carrying anything else is skipped rather than coerced — an unrecognised
 * comparison-set kind means this row was written by something that does not
 * share our understanding of the table, and rendering it as one of the two
 * kinds we do understand would be a guess.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { Database } from '../types/database.generated';
import {
  COMPARISON_SET_KINDS,
  type ComparisonSet,
  type ComparisonSetKind,
  type RejectedAlternative,
} from './rejectedAlternatives';

/**
 * Exactly the columns the read below selects — a `Pick` off the generated Row
 * rather than the whole Row, so adding a column to the select without adding
 * it here (or vice versa) is a compile error instead of a silent `undefined`.
 */
type RejectedAlternativeRow = Pick<
  Database['public']['Tables']['decision_rejected_alternatives']['Row'],
  | 'decision_id'
  | 'item_id'
  | 'comparison_set_kind'
  | 'comparison_set_id'
  | 'rank_at_decision'
  | 'cut_line_position_at_decision'
  | 'cut_reason'
>;

function asComparisonSetKind(raw: string): ComparisonSetKind | null {
  return (COMPARISON_SET_KINDS as readonly string[]).includes(raw)
    ? (raw as ComparisonSetKind)
    : null;
}

export interface RecordAlternativesResult {
  ok: boolean;
  inserted?: number;
  basis?: string;
  error?: string;
}

/**
 * Captures the below-cutline items of a locked comparison set onto a decision.
 *
 * Idempotent for the same set; the RPC refuses a second, different set. The
 * database's refusal sentences are already written in user language, so they
 * are surfaced verbatim rather than translated.
 */
export async function recordRejectedAlternatives(args: {
  decisionId: string;
  comparisonSetKind: ComparisonSetKind;
  comparisonSetId: string;
}): Promise<RecordAlternativesResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('record_rejected_alternatives', {
    p_decision_id: args.decisionId,
    p_comparison_set_kind: args.comparisonSetKind,
    p_comparison_set_id: args.comparisonSetId,
  });

  if (error) return { ok: false, error: error.message };

  const payload = (data ?? {}) as { inserted?: number; basis?: string };
  return { ok: true, inserted: payload.inserted ?? 0, basis: payload.basis };
}

/**
 * The structural alternatives recorded against one decision, titled.
 *
 * Titles come from a second query rather than an embedded join: the table is
 * new and unapplied, so PostgREST cannot yet be relied on to resolve its
 * foreign key by name. Missing titles stay null and the narration falls back
 * — a deleted item must not blank out the whole record of what was beaten.
 */
export async function listAlternativesForDecision(
  decisionId: string,
): Promise<{ rows: RejectedAlternative[]; error?: string }> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db
    .from('decision_rejected_alternatives')
    .select(
      'decision_id, item_id, comparison_set_kind, comparison_set_id, rank_at_decision, cut_line_position_at_decision, cut_reason',
    )
    .eq('decision_id', decisionId)
    .order('rank_at_decision', { ascending: true, nullsFirst: false });

  if (error) return { rows: [], error: error.message };

  const raw: RejectedAlternativeRow[] = data ?? [];
  if (raw.length === 0) return { rows: [] };

  const titles = await fetchItemTitles(raw.map((r) => r.item_id));

  const rows: RejectedAlternative[] = [];
  for (const r of raw) {
    const kind = asComparisonSetKind(r.comparison_set_kind);
    if (!kind) continue;
    rows.push({
      itemId: r.item_id,
      title: titles.get(r.item_id) ?? null,
      rankAtDecision: r.rank_at_decision,
      cutLinePositionAtDecision: r.cut_line_position_at_decision,
      cutReason: r.cut_reason,
      comparisonSetKind: kind,
      comparisonSetId: r.comparison_set_id,
    });
  }
  return { rows };
}

async function fetchItemTitles(itemIds: string[]): Promise<Map<string, string>> {
  const unique = Array.from(new Set(itemIds));
  if (unique.length === 0) return new Map();

  const db = getTypedSupabaseClient();
  const { data } = await db.from('items').select('id, title').in('id', unique);
  return new Map((data ?? []).map((row) => [row.id, row.title]));
}

/**
 * Reads a locked item pool as a comparison set, for the capture-time preview.
 *
 * The preview and the stored record are two independent derivations over the
 * same predicate (`belowCutline`). They agree because the rule lives in one
 * module — but the RPC's derivation, not this one, is what gets recorded.
 */
export async function loadItemPoolComparisonSet(
  itemPoolId: string,
): Promise<{ set: ComparisonSet | null; error?: string }> {
  const db = getTypedSupabaseClient();

  const { data: pool, error: poolError } = await db
    .from('item_pools')
    .select('id, cut_line_position')
    .eq('id', itemPoolId)
    .single();
  if (poolError || !pool) return { set: null, error: poolError?.message ?? 'Item pool not found' };

  const { data: memberships, error: memberError } = await db
    .from('item_pool_memberships')
    .select('item_id, rank, selection_reason')
    .eq('item_pool_id', itemPoolId)
    .order('rank', { ascending: true });
  if (memberError) return { set: null, error: memberError.message };

  const itemIds = (memberships ?? []).map((m) => m.item_id);
  const titles = await fetchItemTitles(itemIds);

  return {
    set: {
      kind: 'item_pool',
      id: pool.id,
      cutLinePosition: pool.cut_line_position,
      members: (memberships ?? []).map((m) => ({
        itemId: m.item_id,
        rank: m.rank,
        title: titles.get(m.item_id),
        cutReason: m.selection_reason,
      })),
    },
  };
}
